import useMobxStores from 'hooks/useMobxStores';
import { useMemo } from 'react';
import isNil from 'lodash/isNil';
import {
  CatalogEntry,
  LearningPath,
  Modality,
  VocabularyItem,
  VocabularyNamespaces,
} from 'types';
import { useTranslation } from 'react-i18next';
import { Duration, DurationUnit } from 'luxon';
import { prioritizeEntryField } from 'stores/common/Filters';
import { TAXONOMIES_PRIORITIES } from 'stores/Catalog/config';
import useCollateralInfo from './useCollateralInfo';

const useOfferingInfo = (entry?: CatalogEntry, learningPath?: LearningPath) => {
  const { i18n, t } = useTranslation();
  const { vocabularyStore, userStore } = useMobxStores();

  const {
    duration,
    duration_unit: durationUnit,
    metadata,
    modality,
    sku,
    slug,
  } = entry ?? ({} as CatalogEntry);

  const { examStatusInfo, techOverviewDuration } = useCollateralInfo({
    modality,
    slug,
    sku,
  });

  const level = useMemo(() => {
    if (!isNil(metadata?.level)) {
      const offeringLevelVocabulary: { [keys: string]: VocabularyItem } =
        vocabularyStore.vocabularyMap[
          `${VocabularyNamespaces.OfferingLevel}@@${i18n.language}`
        ]?.vocabulary;

      if (offeringLevelVocabulary) {
        return Object.values(offeringLevelVocabulary).find(
          (item) => item?.token === metadata?.level.toString(),
        )?.display_name;
      }
    }

    return null;
  }, [metadata?.level, vocabularyStore.vocabularyMap, i18n.language]);

  const offeringProducts = useMemo<string[]>(() => {
    // TODO: Idea: call filter config extractValues for product filter. It has similar logic.

    // this extracts the product codes from the ideal fields
    const products: string[] = prioritizeEntryField<CatalogEntry>(
      entry,
      TAXONOMIES_PRIORITIES.products,
      true,
    );
    if (!isNil(products)) {
      const offeringProductsVocabulary: { [keys: string]: VocabularyItem } =
        vocabularyStore.vocabularyMap[
          `${VocabularyNamespaces.OfferingTaxonomyProducts}@@${i18n.language}`
        ]?.vocabulary;

      if (offeringProductsVocabulary) {
        return products.reduce((productNames, productCode) => {
          if (productCode === 'unknown') return productNames;

          const productName = Object.values(offeringProductsVocabulary).find(
            (item) => item?.token === productCode,
          )?.display_name;

          if (!productName) return productNames;

          return [...productNames, productName];
        }, [] as string[]);
      }
    }

    return [];
  }, [entry, vocabularyStore.vocabularyMap]);

  const offeringTopics = useMemo<string[]>(() => {
    // TODO: Idea: call filter config extractValues for product filter. It has similar logic.

    // this extracts the topic codes from the ideal fields
    const topics: string[] = prioritizeEntryField<CatalogEntry>(
      entry,
      TAXONOMIES_PRIORITIES.topics,
      true,
    );
    if (!isNil(topics)) {
      const offeringTopicsVocabulary: { [keys: string]: VocabularyItem } =
        vocabularyStore.vocabularyMap[
          `${VocabularyNamespaces.OfferingTaxonomyTopics}@@${i18n.language}`
        ]?.vocabulary;

      if (offeringTopicsVocabulary) {
        return topics.reduce((topicNames, topicCode) => {
          if (topicCode === 'unknown') return topicNames;

          const topicName = Object.values(offeringTopicsVocabulary).find(
            (item) => item?.token === topicCode,
          )?.display_name;

          if (!topicName) return topicNames;

          return [...topicNames, topicName];
        }, []);
      }
    }

    return [];
  }, [entry, vocabularyStore.vocabularyMap, i18n.language]);

  const totalDuration = useMemo(() => {
    let durationValue = metadata?.total_duration || 0;
    let durationUnitValue: DurationUnit = 'minutes';

    if (modality === Modality.TechOverview) {
      durationValue = techOverviewDuration.duration || 0;
      durationUnitValue =
        (techOverviewDuration.unit as DurationUnit) || 'minutes';
    } else if (modality === Modality.ExpertSeminar) {
      durationValue = duration || 0;
      durationUnitValue = (durationUnit as DurationUnit) || 'minutes';
    }

    if (!durationValue) return null;
    const shifted = Duration.fromObject({ [durationUnitValue]: durationValue })
      .plus({ minutes: 59 })
      .shiftTo('hours', 'minutes');
    if (shifted.hours > 0) return shifted.toFormat("h'h'");
    return `${Math.floor(shifted.minutes)}min`;
  }, [
    duration,
    durationUnit,
    metadata?.total_duration,
    modality,
    techOverviewDuration?.duration,
    techOverviewDuration?.unit,
  ]);

  const totalLabs = useMemo(() => {
    if (!isNil(metadata?.total_labs) && metadata?.total_labs) {
      return `${metadata.total_labs} ${t('labs')}`;
    }

    return null;
  }, [metadata?.total_labs, t]);

  const totalQuizzes = useMemo(() => {
    if (!isNil(metadata?.total_quiz) && metadata?.total_quiz) {
      return `${metadata?.total_quiz} ${t('Quizzes')}`;
    }

    return null;
  }, [metadata?.total_quiz, t]);

  const totalVideoDuration = useMemo(() => {
    if (!isNil(metadata?.total_video_duration)) {
      const videoDuration = metadata?.total_video_duration;
      if (!videoDuration) return null;
      const shifted = Duration.fromObject({
        minutes: videoDuration,
      }).shiftTo('hours', 'minutes');
      if (shifted.hours > 0) return shifted.toFormat("h'h' mm'min'");
      return `${Math.floor(shifted.minutes)}min`;
    }

    return null;
  }, [metadata?.total_video_duration]);

  const examExperienceQuota = {
    allowed: userStore?.enablementsQuotas?.experiences?.allowed,
    used: userStore?.enablementsQuotas?.experiences?.used,
  };

  const skillsPathOfferings = useMemo(() => {
    if (!isNil(learningPath)) {
      const completedOfferings =
        learningPath?.lp_elements?.filter((el) => (el.user_progress ?? 0) >= 1)
          .length ?? 0;
      return learningPath?.joined
        ? t('{{completed}}/{{total}} complete', {
            completed: completedOfferings,
            total: learningPath?.lp_elements.length,
          })
        : t('{{count}} offerings', { count: learningPath?.lp_elements.length });
    }

    return null;
  }, [learningPath, t]);

  return {
    examStatusInfo,
    examExperienceQuota,
    level,
    offeringProducts,
    offeringTopics,
    totalDuration,
    totalLabs,
    totalQuizzes,
    totalVideoDuration,
    skillsPathOfferings,
  };
};

export default useOfferingInfo;
